import React, { useEffect, useState } from 'react';
import { dashboardAPI, projectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Badge, Skeleton } from '../components/ui';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FolderKanban, CheckSquare, CalendarDays, Activity, MessageSquare, ChevronRight, Clock, Star } from 'lucide-react';
import './Dashboard.css';

const statusColor = {
  Backlog:       '#a78bfa',
  'To Do':       '#38bdf8',
  'In Progress': '#ffd166',
  'In Review':   '#06d6a0',
  Done:          '#06d6a0',
};

const activityTypeColors = {
  note:    '#a78bfa',
  call:    '#38bdf8',
  email:   '#06d6a0',
  meeting: '#ffd166',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-bright)',
      borderRadius: 10, padding: '10px 14px', minWidth: 130,
      boxShadow: 'var(--shadow-card)', backdropFilter: 'blur(10px)'
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', fontFamily: 'var(--font-display)' }}>
        {payload[0].value}
      </div>
    </div>
  );
};

export default function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [projRes, actRes] = await Promise.all([
          projectsAPI.list(),
          dashboardAPI.recentActivities(),
        ]);
        setProjects(projRes.data.data || []);
        setActivities(actRes.data.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const activeProjects = projects.filter(p => p.status !== 'Done');
  const completedProjects = projects.filter(p => p.status === 'Done');
  const dueSoonProjects = projects.filter((project) => {
    if (!project.end_date || project.status === 'Done') return false;
    const dueDate = new Date(project.end_date);
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    return dueDate >= new Date() && dueDate <= sevenDaysFromNow;
  });

  // Prepare data for Pie Chart
  const statusCounts = projects.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});
  
  const pieData = Object.keys(statusCounts).map(status => ({
    name: status,
    value: statusCounts[status],
    fill: statusColor[status] || 'var(--accent)'
  }));

  // Prepare data for Activity Timeline (Bar chart)
  const activityFrequency = activities.slice(0, 15).reduce((acc, act) => {
    const date = new Date(act.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    acc[date] = (acc[date] || 0) + 1;
    return acc;
  }, {});
  const barData = Object.keys(activityFrequency).map(date => ({
    date,
    Updates: activityFrequency[date]
  })).reverse();

  const greeting = user?.name ? `Welcome Back, ${user.name.split(' ')[0]}.` : 'Welcome Back.';

  return (
    <div className="dashboard-modern animate-in">
      <div className="dashboard-modern__header">
        <div>
          <h1 className="dashboard-modern__title">{greeting}</h1>
          <p className="dashboard-modern__subtitle">Here is the latest overview of your projects.</p>
        </div>
      </div>

      <section className="dashboard-modern__stats-row">
        <article className="dashboard-modern__stat-card glass-panel">
          <div className="dashboard-modern__stat-top">
            <div className="dashboard-modern__stat-text">
              <div className="dashboard-modern__stat-label">Active Projects</div>
              <div className="dashboard-modern__stat-value">{loading ? <Skeleton width={40} height={36} /> : activeProjects.length}</div>
            </div>
            <div className="dashboard-modern__stat-icon" style={{ background: 'rgba(108,99,255,0.22)', color: '#8b84ff' }}>
              <FolderKanban size={20} />
            </div>
          </div>
          <div className="dashboard-modern__stat-trend" style={{ color: '#8b84ff', fontSize: 13, fontWeight: 600 }}>Currently in progress</div>
        </article>

        <article className="dashboard-modern__stat-card glass-panel">
          <div className="dashboard-modern__stat-top">
            <div className="dashboard-modern__stat-text">
              <div className="dashboard-modern__stat-label">Completed Projects</div>
              <div className="dashboard-modern__stat-value">{loading ? <Skeleton width={40} height={36} /> : completedProjects.length}</div>
            </div>
            <div className="dashboard-modern__stat-icon" style={{ background: 'rgba(6,214,160,0.22)', color: '#06d6a0' }}>
              <CheckSquare size={20} />
            </div>
          </div>
          <div className="dashboard-modern__stat-trend" style={{ color: '#06d6a0', fontSize: 13, fontWeight: 600 }}>Successfully delivered</div>
        </article>

        <article className="dashboard-modern__stat-card glass-panel">
          <div className="dashboard-modern__stat-top">
            <div className="dashboard-modern__stat-text">
              <div className="dashboard-modern__stat-label">Upcoming Deadlines</div>
              <div className="dashboard-modern__stat-value">{loading ? <Skeleton width={40} height={36} /> : dueSoonProjects.length}</div>
            </div>
            <div className="dashboard-modern__stat-icon" style={{ background: 'rgba(255,209,102,0.22)', color: '#ffd166' }}>
              <CalendarDays size={20} />
            </div>
          </div>
          <div className="dashboard-modern__stat-trend" style={{ color: '#ffd166', fontSize: 13, fontWeight: 600 }}>Due within 7 days</div>
        </article>
      </section>

      <section className="dashboard-modern__middle-row" style={{ gridTemplateColumns: '1fr 2fr' }}>
        {/* Project Status Pie Chart */}
        <div className="dashboard-modern__panel glass-panel">
          <div className="dashboard-modern__panel-header">
            <h2 className="dashboard-modern__panel-title">Project Distribution</h2>
          </div>
          <div className="dashboard-modern__chart-container">
            {loading ? <Skeleton height={220} /> : pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}/>
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-dim)' }}>No project data available.</div>
            )}
          </div>
        </div>

        {/* Project Activity Bar Chart */}
        <div className="dashboard-modern__panel glass-panel">
          <div className="dashboard-modern__panel-header">
            <h2 className="dashboard-modern__panel-title">Recent Activity Frequency</h2>
          </div>
          <div className="dashboard-modern__chart-container">
            {loading ? <Skeleton height={220} /> : barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={barData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-bright)" opacity={0.5} />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} dy={10} />
                  <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                  <Tooltip cursor={{ fill: 'var(--accent-surface)' }} content={<CustomTooltip />} />
                  <Bar dataKey="Updates" radius={[6, 6, 0, 0]} barSize={32} fill="var(--accent)">
                    {barData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.Updates > 2 ? 'var(--color-sky)' : 'var(--accent)'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-dim)' }}>No recent activities.</div>
            )}
          </div>
        </div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginTop: 24 }}>
        
        {/* Active Projects List */}
        <section className="dashboard-modern__panel glass-panel" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="dashboard-modern__panel-header" style={{ padding: '24px 24px 0 24px' }}>
            <h2 className="dashboard-modern__panel-title">My Active Projects</h2>
          </div>
          <div className="dashboard-modern__card-body" style={{ padding: 0 }}>
            {loading ? <div style={{ padding: 24 }}><Skeleton height={80} count={3} /></div> : activeProjects.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {activeProjects.map(project => (
                  <div 
                    key={project.project_id} 
                    onClick={() => navigate(`/app/projects?id=${project.project_id}`)}
                    style={{ 
                      padding: '20px 24px', borderBottom: '1px solid var(--border)', 
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      cursor: 'pointer', transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.paddingLeft = '32px'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.paddingLeft = '24px'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--accent-surface)', color: 'var(--accent)', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 18 }}>
                        {project.project_name.charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                          {project.project_name}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: 'var(--text-muted)' }}>
                          <span>Due {project.end_date ? new Date(project.end_date).toLocaleDateString() : '—'}</span>
                          {project.manager_id && project.manager && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <MessageSquare size={12} /> {project.manager.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <Badge value={project.status} style={{ background: statusColor[project.status] ? `${statusColor[project.status]}22` : 'var(--bg-hover)', color: statusColor[project.status] || 'var(--text)', padding: '6px 12px', fontSize: 12 }} />
                      <ChevronRight size={20} color="var(--text-muted)" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>
                You have no active projects at the moment.
              </div>
            )}
          </div>
        </section>

        {/* Recent Updates */}
        <section className="dashboard-modern__panel glass-panel">
          <div className="dashboard-modern__panel-header">
            <h2 className="dashboard-modern__panel-title">Recent Feed</h2>
          </div>
          <div className="dashboard-modern__card-body" style={{ padding: 0 }}>
            {loading ? <Skeleton height={60} count={4} /> : activities.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, position: 'relative' }}>
                <div style={{ position: 'absolute', left: 19, top: 10, bottom: 10, width: 2, background: 'var(--border)' }} />
                {activities.slice(0, 5).map(act => {
                  const typeColor = activityTypeColors[act.type] || '#6c63ff';
                  return (
                    <div key={act.activity_id} style={{ display: 'flex', gap: 16, position: 'relative', zIndex: 1 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%', background: 'var(--bg-card)',
                        border: `2px solid ${typeColor}`, display: 'grid', placeItems: 'center',
                        color: typeColor, flexShrink: 0, boxShadow: `0 0 12px ${typeColor}33`
                      }}>
                        <Activity size={16} />
                      </div>
                      <div style={{ flex: 1, paddingTop: 4 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                          {act.title || act.type}
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 6 }}>
                          {act.description}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                          <Clock size={12} />
                          {new Date(act.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-dim)' }}>
                No recent updates available.
              </div>
            )}
          </div>
        </section>

      </div>
    </div>
  );
}